import HomeProductCard from "./HomeProductCard";
import "./HomeProductSection.css";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { useState, useEffect, useRef } from "react";
import AliceCarousel from "react-alice-carousel";
import api from "../../../config/api";

// A homepage product row backed by a flat endpoint (New Arrivals / Best Sellers)
// rather than a category. Hides itself when the endpoint returns nothing.
const HomeCuratedSection = ({ heading, eyebrow, endpoint, className = "" }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [products, setProducts] = useState([]);
  const carouselRef = useRef(null);

  useEffect(() => {
    let active = true;
    api
      .get(endpoint)
      .then(({ data }) => active && setProducts(Array.isArray(data) ? data : []))
      .catch(() => active && setProducts([]));
    return () => {
      active = false;
    };
  }, [endpoint]);

  const syncActiveIndex = ({ item }) => setActiveIndex(item);
  const handlePrevClick = () => carouselRef.current && carouselRef.current.slidePrev();
  const handleNextClick = () => carouselRef.current && carouselRef.current.slideNext();

  // Fractional item counts leave part of the next card visible ("peek"). With a
  // whole number the row ended flush at the viewport edge and looked like a
  // static grid — there are no arrows or dots below lg, so the peek is the only
  // cue that the row scrolls. Desktop already did this with 4.5.
  const responsive = {
    0: { items: 2.2, itemsFit: "contain" },
    568: { items: 3.2, itemsFit: "contain" },
    1024: { items: 4.5, itemsFit: "contain" },
  };

  const content = products;
  // Nothing to show -> render NOTHING. The section's vertical padding lives on
  // this element (not on a wrapper in Homepage), otherwise an empty row still
  // reserved its padding and left a blank band on the page.
  if (!content.length) return null;

  return (
    <div className={`mx-auto max-w-7xl px-4 lg:px-8 ${className}`}>
      {(eyebrow || heading) && (
        <div className="mb-6">
          {eyebrow && (
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#b28585]">
              {eyebrow}
            </p>
          )}
          {heading && (
            <h2 className="font-display text-3xl font-bold text-[#511d00] sm:text-4xl">
              {heading}
            </h2>
          )}
        </div>
      )}

      <div className="relative">
        <AliceCarousel
          disableButtonsControls
          disableDotsControls
          mouseTracking
          items={content.slice(0, 12).map((item) => (
            <div key={item._id} className="px-2">
              <HomeProductCard
                product={item}
                category={item.thirdLavelCategory?.name || "shop"}
                view_all
              />
            </div>
          ))}
          activeIndex={activeIndex}
          responsive={responsive}
          onSlideChanged={syncActiveIndex}
          animationType="fadeout"
          animationDuration={250}
          ref={carouselRef}
        />

        <div className="hidden lg:block">
          {activeIndex !== 0 && (
            <button
              onClick={handlePrevClick}
              className="absolute left-0 top-[40%] z-10 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[#eadcc0] bg-white text-[#5e3e3e] shadow-md transition hover:bg-[#f8ecd7]"
            >
              <ChevronLeftRoundedIcon />
            </button>
          )}
          {content.length > 5 && activeIndex < content.length - 4 && (
            <button
              onClick={handleNextClick}
              className="absolute right-0 top-[40%] z-10 flex h-11 w-11 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full border border-[#eadcc0] bg-white text-[#5e3e3e] shadow-md transition hover:bg-[#f8ecd7]"
            >
              <ChevronRightRoundedIcon />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomeCuratedSection;
